<template>
  <el-input
    :size="sizeMap[props.size]"
    :class="[style.input, `input-${props.size}`, `input-${props.align}`, props.filled ? `input-filled` : '', slots.prepend || slots.append ? 'input-insert' : '', error ? 'input-error' : '']"
  >
    <slot />

    <template v-if="slots.prepend" #prefix>
      <div :class="style.insert">
        <slot name="prepend" />
        <span class="input-prefix-line" />
      </div>
    </template>
    <template v-else #prefix>
      <slot name="prefix" />
    </template>
    <template v-if="slots.append" #suffix>
      <div :class="style.insert">
        <span class="input-prefix-line" />
        <slot name="append" />
      </div>
    </template>
    <template v-else #suffix>
      <slot name="suffix" />
    </template>
  </el-input>
</template>

<script setup lang="ts">
const slots = useSlots();
const props = defineProps({
  size: {
    validator(value: string) {
      return ['max', 'large', 'default', 'small', 'mini'].indexOf(value) > -1;
    },
    type: String,
    default: 'default'
  },
  filled: {
    type: Boolean,
    default: false
  },
  align: {
    type: String,
    default: 'left'
  },
  error: {
    type: Boolean,
    default: false
  }
});

const sizeMap: any = reactive({
  max: 'large',
  large: 'large',
  default: 'default',
  small: 'small',
  mini: 'small'
});

</script>

<style lang="scss" module="style">
.input {
  --el-input-border-color: var(--border-border1);
  --el-input-bg-color: transparent;
  --el-disabled-bg-color: none;
  --el-disabled-border-color: var(--border-border1);
  --el-disabled-text-color: var(--text-text5);
  --el-fill-color-blank: var(--fill-fill3);
  --el-text-color-regular: var(--text-text1);
  --text-align:left;
  height: var(--el-input-height);
  &:hover {
    --el-input-hover-border-color: var(--gray-gray10);
  }

  :global(.el-input__wrapper) {
    --el-input-focus-border-color: var(--gray-gray10);
  }

  :global(.el-input__inner::placeholder) {
    color: var(--text-text4);
  }

  :global {
    .el-input__prefix {
      color: var(--text-text4);
    }

    .el-input__suffix {
      color: var(--text-text4);
    }
    .input-prefix-line {
      display: inline-block;
      width: 1px;
      height: 1.3em;
      background: var(--border-border1);
    }
    .is-focus{
      --prefix-line : var(--el-input-focus-border-color);
    }
  }

  :global(.el-input__inner) {
    font-size: var(--font-size);
    font-weight: var(--font-weight);
    text-align: var(--text-align);
  }

  &:global{
    &.el-textarea{
      --el-input-height: auto;
      --el-input-focus-border-color: var(--gray-gray10);
    }
  }

}

.insert {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

:global {
  .input {

    &-max {
      --el-border-radius-base: var(--radius-max);
      --font-size: var(--font-size-max);
      --font-weight: var(--font-weight-max);
      --el-input-height: var(--height-max);
    }

    &-large {
      --el-border-radius-base: var(--radius-large);
      --font-size: var(--font-size-large);
      --font-weight: var(--font-weight-large);
      --el-input-height: var(--height-large);
    }

    &-default {
      --el-border-radius-base: var(--radius-default);
      --font-size: var(--font-size-default);
      --font-weight: var(--font-weight-default);
      --el-input-height: var(--height-default);
    }

    &-small {
      --el-border-radius-base: var(--radius-small);
      --font-size: var(--font-size-small);
      --font-weight: var(--font-weight-small);
      --el-input-height: var(--height-small);
    }

    &-mini {
      --el-border-radius-base: var(--radius-mini);
      --font-size: var(--font-size-mini);
      --font-weight: var(--font-weight-mini);
      --el-input-height: var(--height-mini);
    }

    &-filled {
      &.is-disabled {
        --el-input-border-color: var(--fill-fill2);
        --el-border-color: var(--fill-fill2);
        --el-input-bg-color: var(--fill-fill2);
        --el-disabled-bg-color: var(--fill-fill2);
        --el-disabled-border-color: var(--fill-fill2);
        --el-disabled-text-color: var(--text-text4);
      }

      .el-input__wrapper {
        --el-input-border-color: var(--fill-fill4);
        --el-border-color: var(--fill-fill4);
        --el-input-bg-color: var(--fill-fill4);
      }

      .el-input__inner {
        background: none;
      }
    }

    &-filled {
      &.is-disabled {
        --el-input-border-color: var(--fill-fill2);
        --el-border-color: var(--fill-fill2);
        --el-input-bg-color: var(--fill-fill2);
        --el-disabled-bg-color: var(--fill-fill2);
        --el-disabled-border-color: var(--fill-fill2);
        --el-disabled-text-color: var(--text-text4);
      }

      .el-input__wrapper {
        --el-input-border-color: var(--fill-fill4);
        --el-border-color: var(--fill-fill4);
        --el-input-bg-color: var(--fill-fill4);
      }

      .el-input__inner {
        background: none;
      }
    }

    &-right {
      --text-align: right;
    }

    &-center {
      --text-align: center;
    }
    &-error{
      .el-input__wrapper{
        --el-input-border-color:var(--danger-danger1);
        --el-input-focus-border-color:var(--danger-danger1);
        --el-input-hover-border-color:var(--danger-danger1);
      }
      
    }
  }

  [dir=rtl] {
    .input {
      &-left {
        --text-align: right;
      }
      &-right {
        --text-align: left;
      }
    }
  }
  
}

[dir=rtl] {
  .input {
    :global {
      .el-input__suffix-inner>:first-child{
        margin-right: 8px;
        margin-left: 0;
      }
      .el-input__prefix-inner>:last-child{
        margin-right: 0;
        margin-left: 8px;
      }
    }
    
  }
}
</style>